<script setup lang="ts">
import useStore from '@/store/auth'
import logoEveExpert from '@/assets/logos/logo-expert.svg'
import logoEveExpertMobile from '@/assets/logos/logo-expert-mobile.svg'
import logoEveGuest from '@/assets/logos/logo-guest.svg'
import logoEveGuestMobile from '@/assets/logos/logo-guest-mobile.svg'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { ref, onMounted, onUnmounted, watch, computed, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import KeycloakService from '@/security/keycloakService'
import { keycloakInstance } from '@/security/instance'
import { useGuestStore } from '@/modules/guest/store'

interface UserBasic {
  id?: string
  fullName?: string
  email?: string
  profilePicture?: string
  name?: string
}

interface KeycloakLike {
  authenticated?: boolean
  token?: string | null
}

interface KeycloakServiceLike {
  keycloak?: KeycloakLike | null
  CallLogin: (cb: () => void) => Promise<void> | void
}

interface MenuOption {
  key: string
  label: string
  icon: string
  target?: string
  action?: () => void
}

const props = withDefaults(
  defineProps<{
    showMenu?: boolean
    brand?: 'auto' | 'guest' | 'expert'
  }>(),
  {
    showMenu: true,
    brand: 'auto',
  }
)

const emit = defineEmits<{ (e: 'toggle-drawer'): void }>()

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const kcSvc = KeycloakService as KeycloakServiceLike
const guestStore = useGuestStore()

const authStore = useStore()
const { user } = storeToRefs(authStore) as { user: Ref<UserBasic | undefined> }
const { logout, getUserInfo } = authStore

const { mobile } = useDisplay()
const isMobile = computed<boolean>(() => mobile.value)
const showMenu = computed<boolean>(() => props.showMenu ?? true)

const isGuestRoute = computed<boolean>(() => {
  if (props.brand === 'guest') return true
  if (props.brand === 'expert') return false

  const name = typeof route.name === 'string' ? route.name : ''
  const byPath = route.path.startsWith('/guest') || name.startsWith('guest')
  const byMeta = route.matched.some(
    (r) =>
      r.meta?.brand === 'guest' ||
      r.meta?.layout === 'public' ||
      r.meta?.public === true
  )

  return byPath || byMeta
})

const hasUnreadGuestNotifications = computed<boolean>(() => {
  return isGuestRoute.value && guestStore.hasUnreadNotifications
})

const logoSrc = ref<string>('')

function resolveLogo() {
  const guest = isGuestRoute.value
  logoSrc.value = guest
    ? isMobile.value
      ? logoEveGuestMobile
      : logoEveGuest
    : isMobile.value
      ? logoEveExpertMobile
      : logoEveExpert
}

function handleLogoError() {
  logoSrc.value = isGuestRoute.value ? logoEveGuest : logoEveExpert
}

watch([isGuestRoute, isMobile], resolveLogo, { immediate: true })

const kcAuth = ref<boolean>(!!keycloakInstance.authenticated)
const kcToken = ref<string | null>(keycloakInstance.token ?? null)
let kcTimer: number | undefined

function syncKcSnapshot() {
  const a = !!keycloakInstance.authenticated
  const t = keycloakInstance.token ?? null

  if (a !== kcAuth.value || t !== kcToken.value) {
    kcAuth.value = a
    kcToken.value = t
  }
}

const isAuthenticated = computed<boolean>(() => {
  return Boolean(user.value?.id) || kcAuth.value || Boolean(kcToken.value)
})

function joinApi(u: string) {
  const base = (import.meta.env.VITE_API_URL as string)?.replace(/\/+$/, '')
  const path = u.startsWith('/') ? u : `/${u}`
  return `${base}${path}`
}

function isAbsolute(u?: string | null) {
  return !!u && /^https?:\/\//i.test(u)
}

function isFromApiHost(url: string) {
  const api = (import.meta.env.VITE_API_URL as string)?.replace(/\/+$/, '')
  try {
    const u = new URL(url, window.location.origin)
    const a = new URL(api)
    return u.origin === a.origin
  } catch {
    return false
  }
}

const avatarUrl = ref<string>('')
let avatarBlobUrl: string | null = null

const userInitials = computed<string>(() => {
  const u = user.value
  const raw = u?.fullName || u?.name || u?.email || ''
  const name = String(raw).trim()

  if (!name) return ''

  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return (
      (parts[0][0] || '') + (parts[parts.length - 1][0] || '')
    ).toUpperCase()
  }

  const beforeAt = name.split('@')[0] || name
  const letters = beforeAt.replace(/[^A-Za-zÀ-ÿ]/g, '')
  return (letters.slice(0, 2) || name.slice(0, 1)).toUpperCase()
})

const externalFallbackSrc = computed(() =>
  buildSvgAvatarDataUrl(
    user.value?.fullName || user.value?.name || user.value?.email,
    128
  )
)

const finalAvatarSrc = computed<string>(() => {
  return avatarUrl.value || externalFallbackSrc.value || ''
})

async function resolveAvatar() {
  if (avatarBlobUrl) {
    URL.revokeObjectURL(avatarBlobUrl)
    avatarBlobUrl = null
  }

  avatarUrl.value = ''

  if (!isAuthenticated.value) return

  const src = user.value?.profilePicture
  if (!src) return

  if (isAbsolute(src) && !isFromApiHost(src)) {
    avatarUrl.value = src
    return
  }

  const url = isAbsolute(src) ? src : joinApi(src)

  if (!isFromApiHost(url)) {
    avatarUrl.value = ''
    return
  }

  try {
    const token = keycloakInstance.token
    const resp = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })

    if (!resp.ok) throw new Error(`HTTP ${resp.status}`)

    const blob = await resp.blob()
    avatarBlobUrl = URL.createObjectURL(blob)
    avatarUrl.value = avatarBlobUrl
  } catch {
    avatarUrl.value = ''
  }
}

const menu = ref<boolean>(false)

function goToGuestHome() {
  menu.value = false

  if (router.hasRoute('guest')) router.push({ name: 'guest' })
  else router.push('/guest')
}

function goToGuestNotifications() {
  menu.value = false

  if (router.hasRoute('guest-notifications')) {
    router.push({ name: 'guest-notifications' })
    return
  }

  router.push('/guest/notifications')
}

const menuOptions = computed<MenuOption[]>(() => {
  const baseKc = (import.meta.env.VITE_KEYCLOAK_URL as string)?.replace(
    /\/+$/,
    ''
  )
  const realm = import.meta.env.VITE_KEYCLOAK_REALM as string
  const accountUrl = `${baseKc}/realms/${realm}/account/`

  const list: MenuOption[] = [
    {
      key: 'my-account',
      label: t('header.myAccount'),
      icon: 'mdi-account-outline',
      target: accountUrl,
    },
  ]

  if (isGuestRoute.value) {
    list.push(
      {
        key: 'my-events',
        label: t('guest.home.title') || 'Meus eventos',
        icon: 'mdi-calendar-month',
        action: goToGuestHome,
      },
      {
        key: 'notifications',
        label: t('header.notifications') || 'Notificações',
        icon: 'mdi-bell-outline',
        action: goToGuestNotifications,
      }
    )
  }

  list.push({
    key: 'logout',
    label: t('header.exit'),
    icon: 'mdi-exit-to-app',
    action: logout,
  })

  return list
})

async function handleAvatarClick() {
  if (!isAuthenticated.value) {
    await kcSvc.CallLogin(() => {})
    return
  }

  menu.value = true
}

function handleAction(option: MenuOption) {
  menu.value = false

  if (option.target) {
    getUserInfo().catch(() => {})
    window.open(option.target, '_blank')
    return
  }

  option.action?.()
}

function handleResize() {
  menu.value = false
}

function goHomeByContext() {
  menu.value = false
  const isGuestContext = isGuestRoute.value || !showMenu.value

  if (isGuestContext) {
    if (router.hasRoute('guest')) router.push({ name: 'guest' })
    else router.push('/guest')
    return
  }

  const candidates = ['expert-home', 'home', 'expert', 'dashboard']
  const targetName = candidates.find((n) => router.hasRoute(n))

  if (targetName) router.push({ name: targetName })
  else router.push('/')
}

onMounted(async () => {
  window.addEventListener('resize', handleResize)
  kcTimer = window.setInterval(syncKcSnapshot, 1000)
  syncKcSnapshot()

  if ((kcAuth.value || kcToken.value) && !user.value) {
    try {
      await getUserInfo()
    } catch {
      //
    }
  }

  await resolveAvatar()

  if (isGuestRoute.value && isAuthenticated.value) {
    guestStore.getNotifications().catch(() => {})
  }
})

function pickColorFromName(name: string) {
  const colors = [
    '#F48FB1',
    '#CE93D8',
    '#9FA8DA',
    '#81D4FA',
    '#80DEEA',
    '#A5D6A7',
    '#E6EE9C',
    '#FFCC80',
    '#FFAB91',
    '#BCAAA4',
    '#B0BEC5',
  ]

  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }

  const idx = Math.abs(hash) % colors.length
  return colors[idx]
}

function buildSvgAvatarDataUrl(nameRaw?: string, size = 128) {
  const name = (nameRaw || 'User Account').trim()
  const parts = name.split(/\s+/).filter(Boolean)

  const initials =
    parts.length >= 2
      ? ((parts[0][0] || '') + (parts[parts.length - 1][0] || '')).toUpperCase()
      : (name.split('@')[0] || name)
          .replace(/[^A-Za-zÀ-ÿ]/g, '')
          .slice(0, 2)
          .toUpperCase()

  const bg = pickColorFromName(name)
  const fontSize = Math.round(size * 0.42)

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
      <rect width="100%" height="100%" rx="${Math.round(size / 2)}" fill="${bg}"/>
      <text x="50%" y="50%" dy="0.36em" text-anchor="middle"
            font-family="system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Helvetica, Arial, Apple Color Emoji, Segoe UI Emoji"
            font-size="${fontSize}" font-weight="700" fill="#ffffff">${initials}</text>
    </svg>`

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`
}

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)

  if (kcTimer !== undefined) {
    clearInterval(kcTimer)
    kcTimer = undefined
  }

  if (avatarBlobUrl) {
    URL.revokeObjectURL(avatarBlobUrl)
    avatarBlobUrl = null
  }
})

watch(
  [isAuthenticated, () => user.value?.profilePicture],
  () => void resolveAvatar()
)

watch(menu, (isOpen) => {
  document.body.classList.toggle('no-scroll', isOpen)
})

const shouldShowDrawerButton = computed<boolean>(() => {
  return logoSrc.value !== logoEveGuest
})
</script>

<template>
  <div v-if="menu" class="user-menu-overlay" @click="menu = false" />

  <v-app-bar
    app
    color="grey-darken-4"
    elevation="2"
    class="position-fixed px-4"
  >
    <v-app-bar-nav-icon
      v-if="showMenu && shouldShowDrawerButton"
      id="navbar-menu-button"
      aria-label="Abrir menu lateral"
      class="text-white"
      @click="emit('toggle-drawer')"
    />

    <div
      class="nav-logo-container h-100"
      role="button"
      tabindex="0"
      @click="goHomeByContext"
      @keydown.enter="goHomeByContext"
      @keydown.space.prevent="goHomeByContext"
    >
      <v-img
        :src="logoSrc"
        alt="Logo"
        height="40"
        width="auto"
        :eager="true"
        @error="handleLogoError"
      />
    </div>

    <v-spacer />

    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      location="bottom end"
      offset-y
      content-class="navbar-menu-user-dialog"
    >
      <template #activator="{ props: activatorProps }">
        <v-btn
          v-bind="activatorProps"
          id="navbar-user-info"
          aria-label="Abrir menu do usuário"
          icon
          @click.stop.prevent="handleAvatarClick"
        >
          <div class="user-avatar-container">
            <img
              v-if="finalAvatarSrc"
              :src="finalAvatarSrc"
              :alt="$t('header.altImage')"
              class="user-avatar"
              @error="avatarUrl = ''"
            />
            <v-avatar
              v-else-if="userInitials"
              size="32"
              color="primary"
              class="user-avatar-fallback"
            >
              <span class="avatar-initials">{{ userInitials }}</span>
            </v-avatar>
            <v-icon v-else color="green" size="28">mdi-account-circle</v-icon>

            <span
              v-if="hasUnreadGuestNotifications"
              class="avatar-notification-dot"
            />
          </div>
        </v-btn>
      </template>

      <v-card class="responsive-card">
        <v-card class="pa-2 ma-2" color="primaryHoverOutline" flat>
          <div class="user-info-container">
            <div class="user-avatar-large">
              <img
                v-if="finalAvatarSrc"
                :src="finalAvatarSrc"
                :alt="$t('header.altImage')"
                class="user-avatar-large-img"
                @error="avatarUrl = ''"
              />
              <v-avatar
                v-else-if="userInitials"
                size="56"
                color="primary"
                class="user-avatar-large-fallback"
              >
                <span class="avatar-initials-lg">{{ userInitials }}</span>
              </v-avatar>
              <v-icon v-else color="grey" size="40">mdi-account-circle</v-icon>
            </div>

            <div class="user-name">
              {{ user?.fullName }}
            </div>

            <div class="user-email">
              {{ user?.email }}
            </div>
          </div>
        </v-card>

        <v-list density="comfortable">
          <template v-for="(option, idx) in menuOptions" :key="option.key">
            <v-list-item class="list-item" @click="handleAction(option)">
              <template #prepend>
                <div class="menu-icon-wrapper">
                  <v-icon size="26">{{ option.icon }}</v-icon>
                  <span
                    v-if="
                      option.key === 'notifications' &&
                      hasUnreadGuestNotifications
                    "
                    class="notification-dot"
                  />
                </div>
              </template>

              <v-list-item-title>{{ option.label }}</v-list-item-title>
            </v-list-item>

            <v-divider v-if="idx < menuOptions.length - 1" inset class="mx-2" />
          </template>
        </v-list>
      </v-card>
    </v-menu>
  </v-app-bar>
</template>

<style scoped>
.nav-logo-container {
  display: flex;
  align-items: center;
  padding-left: 16px;
  margin-left: 8px;
}

.responsive-card {
  top: 10px;
  width: 300px;
}

.responsive-card > .v-card {
  border-radius: 12px !important;
}

.v-list-item--density-compact.v-list-item--one-line {
  min-height: 56px;
}

.list-item:hover {
  background-color: var(--v-theme-primaryHoverOutline);
}

.user-avatar-container {
  position: relative;
  margin: 0 16px;
  display: flex;
  height: 32px;
  width: 32px;
  align-items: center;
  justify-content: center;
  border-radius: 24px;
  background-color: white;
}

.user-avatar {
  border-radius: 24px;
  object-fit: cover;
  width: 32px;
  height: 32px;
}

.user-avatar-large {
  margin-bottom: 8px;
  display: flex;
  height: 56px;
  width: 56px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: white;
}

.user-avatar-large-img {
  border-radius: 50%;
  object-fit: cover;
  width: 56px;
  height: 56px;
}

.user-avatar-fallback,
.user-avatar-large-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-initials {
  color: #fff;
  font-weight: 600;
  font-size: 0.8rem;
  line-height: 1;
}

.avatar-initials-lg {
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  line-height: 1;
}

.user-info-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.user-name {
  font-weight: 500;
  color: #000000de;
}

.user-email {
  font-weight: 400;
  font-size: 0.875rem;
  color: #00000099;
}

.user-menu-overlay {
  position: fixed;
  width: 100%;
  height: 100%;
  background: rgba(33, 33, 33, 0.46);
  z-index: 9;
  overflow: hidden !important;
}

.no-scroll {
  overflow: hidden !important;
  position: fixed !important;
  width: 100%;
}

.navbar-menu-user-dialog {
  top: 64px !important;
  border-radius: 16px !important;
}

.menu-icon-wrapper {
  position: relative;
  width: 32px;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.notification-dot {
  position: absolute;
  top: 1px;
  right: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #fb8c00;
  border: 2px solid white;
}

.avatar-notification-dot {
  position: absolute;
  top: -1px;
  right: -1px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #fb8c00;

  z-index: 1;
}

@media (max-width: 960px) {
  .responsive-card {
    width: 100%;
  }

  .navbar-menu-user-dialog {
    width: -webkit-fill-available;
  }
}
</style>
